import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  LogOut,
  CheckCircle,
  Globe,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Home,
  Coins,
  Share2,
  Rocket,
  Bell,
  Users,
  Vote,
  Wallet,
  Crown,
  Package,
  Palette,
  Store,
  Sparkles,
  Trophy,
  Pickaxe,
  HelpCircle,
  Flame,
  Gem,
  type LucideIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sendLightHapticFeedback } from '../utils/haptics';
import { useAuth } from '../contexts/AuthContext';
import { MiniKit, WalletAuthInput } from '@worldcoin/minikit-js';
import { useToast } from './ToastContainer';
import { supabase } from '../lib/supabaseClient';
import { formatNumberForDisplay } from '../utils/formatters';
import { useBalanceRefresh } from '../contexts/BalanceRefreshContext';
import { useActiveCharacter } from '../game/hooks/useActiveCharacter';
import { CharacterAvatar } from '../game/components/CharacterAvatar';
import { getUserInGameWallets } from '../lib/inGameWallet';

interface HeaderProps {
  isWorldApp: boolean;
  isMiniKitReady: boolean;
}

interface HeaderNavLink {
  name: string;
  path: string;
  icon: LucideIcon;
  state?: Record<string, unknown>;
}

interface SocialNavLink {
  name: string;
  icon: string;
  url: string;
}

const HEADER_Z_INDEX = 40;
const HEADER_OVERLAY_Z_INDEX = 50;
const HEADER_DROPDOWN_Z_INDEX = 50;

const Header: React.FC<HeaderProps> = ({ isWorldApp, isMiniKitReady }) => {
  const BBG_TOKEN_ICON =
    'https://hmseykssvtyrihuxymeo.supabase.co/storage/v1/object/public/bbt/public/image/logo/bbg_token.png';
  const { user, logout, isLoading, markAsExistingUser, loginWithGoogle } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [_isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isSocialMenuOpen, setIsSocialMenuOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [bbgBalance, setBbgBalance] = useState<string | null>(null);
  const [registeredUserCount, setRegisteredUserCount] = useState<number | null>(null);

  const socialMenuRef = useRef<HTMLDivElement>(null);
  const { refreshKey } = useBalanceRefresh();
  const { activeCharacter } = useActiveCharacter(user?.id);

  const languageOptions = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'zh', label: '繁體中文', flag: '🇭🇰' },
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
    { code: 'ko', label: '한국어', flag: '🇰🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' }
  ] as const;

  // 已移除社交 icon 顯示：保留 Share dropdown 只顯示 nav icons
  // const socialMediaLinks = [
  //   { platform: 'X.COM', icon: 'image/logo/X.png', url: 'https://x.com/BubbleGameBBG' },
  //   { platform: 'Telegram', icon: 'image/logo/telegram.png', url: 'https://t.me/BubbleTeaCoinCommunity' }
  // ];

  useEffect(() => {
    const fetchBBGBalance = async () => {
      if (!user?.id) {
        setBbgBalance(null);
        return;
      }

      try {
        console.log('🪙 [Header] Fetching in-game BBG balance for user:', user.id);
        const inGameWallets = await getUserInGameWallets(user.id, { forceRefresh: true });
        const bbgWallet = inGameWallets.find((wallet) => wallet.symbol === 'BBG');
        const balance = bbgWallet?.balance ?? '0';
        setBbgBalance(formatNumberForDisplay(balance));
        console.log('✅ [Header] In-game BBG balance fetched:', formatNumberForDisplay(balance));
      } catch (error) {
        console.error('❌ [Header] Error fetching in-game BBG balance:', error);
        setBbgBalance('0');
      }
    };

    fetchBBGBalance();
  }, [user?.id, refreshKey]);

  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const { data, error } = await supabase.rpc('get_registered_user_count');
        if (error) throw error;
        setRegisteredUserCount(data as number);
      } catch (err) {
        console.error('❌ Error fetching registered user count:', err);
        setRegisteredUserCount(null);
      }
    };

    fetchUserCount();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (socialMenuRef.current && !socialMenuRef.current.contains(event.target as Node)) {
        setIsSocialMenuOpen(false);
      }
    };

    if (isSocialMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSocialMenuOpen]);

  useEffect(() => {
    if (user && isVerifying) {
      setIsVerifying(false);
    }
  }, [user, isVerifying]);

  const handleMiniKitVerification = async () => {
    if (!isWorldApp) {
      showError(t('common.error'), t('toast.pleaseUseWorldApp'));
      return;
    }

    try {
      setIsVerifying(true);

      const nonceResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-nonce`, {
        method: 'GET'
      });

      if (!nonceResponse.ok) throw new Error(`獲取 nonce 失敗: ${nonceResponse.status}`);

      const { nonce } = await nonceResponse.json();

      const walletAuthInput: WalletAuthInput = {
        nonce,
        expirationTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        notBefore: new Date(Date.now() - 24 * 60 * 60 * 1000),
        statement: '使用您的錢包登入珍珠奶茶幣應用程式'
      };

      const { finalPayload } = await MiniKit.commandsAsync.walletAuth(walletAuthInput);

      if (finalPayload.status === 'error') {
        throw new Error('details' in finalPayload ? finalPayload.details : '錢包驗證被拒絕');
      }

      const walletAddress = MiniKit.walletAddress || finalPayload.address;
      const username = MiniKit.user?.username;

      if (!walletAddress) throw new Error('無法獲取錢包地址');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-wallet-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_GG_PUBLISHABLE_KEY
        },
        body: JSON.stringify({ payload: finalPayload, nonce, walletAddress, username })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`${t('toast.orbVerificationFailed')}: ${errorData.details || response.status}`);
      }

      const verificationResult = await response.json();

      if (!verificationResult.success) {
        throw new Error(`${t('toast.orbVerificationFailed')}: ${verificationResult.details || t('common.unknownError')}`);
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: verificationResult.email,
        password: verificationResult.password
      });

      if (signInError) throw new Error(`登入失敗: ${signInError.message}`);

      const addrShort = `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
      if (verificationResult.isNewUser) {
        showSuccess(t('toast.walletVerificationSuccess'), `${t('toast.welcomeNewUser')}\n${t('toast.walletPrefix')}${addrShort}`);
      } else {
        showSuccess(t('toast.walletVerificationSuccess'), `${t('toast.welcomeBack')}\n${t('toast.walletPrefix')}${addrShort}`);
        try {
          await markAsExistingUser();
        } catch (e) {
          console.warn('Failed to mark user as existing:', e);
        }
      }
    } catch (error) {
      console.error('[Header MiniKit] Wallet Auth error:', error);
      const errorMessage = error instanceof Error ? error.message : t('common.unknownError');
      showError(t('toast.walletAuthFailed'), errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleLogout = async () => {
    await sendLightHapticFeedback();
    logout();
    setIsMenuOpen(false);
  };

  const changeLanguage = async (lng: string) => {
    await sendLightHapticFeedback();
    i18n.changeLanguage(lng);
    setIsLanguageMenuOpen(false);
    setIsSocialMenuOpen(false);
    setIsMenuOpen(false);
  };

  const gameNavLinks: HeaderNavLink[] = [
    { name: 'characterLibrary', path: '/characters', icon: Users },
    { name: 'itemInventory', path: '/items', icon: Package },
    { name: 'appearance', path: '/appearance', icon: Palette },
    { name: 'bubbleShop', path: '/shop', icon: Store },
    { name: 'characterSynthesis', path: '/synthesis', icon: Sparkles },
    { name: 'throneBattle', path: '/throne-battle', icon: Crown },
    { name: 'powerLeaderboard', path: '/leaderboard', icon: Trophy },
    { name: 'mining', path: '/mining-map', icon: Pickaxe },
    { name: 'furnace', path: '/furnace', icon: Flame },
    ...(import.meta.env.DEV
      ? [{ name: 'relicFusion', path: '/relic-fusion', icon: Gem }]
      : [])
  ];

  const navLinks: HeaderNavLink[] = [
    { name: 'notice', path: '/notice', icon: Bell },
    { name: 'airdrop', path: '/airdrop', icon: Rocket },
    { name: 'gameGuide', path: '/game', icon: HelpCircle, state: { openGuide: true } },
    { name: 'dex', path: '/dex', icon: Coins },
    { name: 'vote', path: '/vote', icon: Vote },
    { name: 'profile', path: '/profile', icon: User },
    { name: 'wallet', path: '/wallet', icon: Wallet }
  ];

  const socialLinks: SocialNavLink[] = [
    {
      name: 'telegram',
      icon: '/image/logo/telegram.png',
      url: 'https://t.me/BubbleTeaCoinCommunity'
    },
    {
      name: 'xcom',
      icon: '/image/logo/X.png',
      url: 'https://x.com/BubbleGameBBG'
    }
  ];

  const getVerificationBadge = () => {
    if (!user?.world_id_credential_type) return null;

    switch (user.world_id_credential_type) {
      case 'device':
        return (
          <CheckCircle className="w-3 h-3 text-yellow-500">
            <title>{t('header.verificationBadge.device')}</title>
          </CheckCircle>
        );
      case 'orb':
        return (
          <CheckCircle className="w-3 h-3 text-green-500">
            <title>{t('header.verificationBadge.orb')}</title>
          </CheckCircle>
        );
      case 'orb_plus':
        return (
          <CheckCircle className="w-3 h-3 text-blue-500">
            <title>{t('header.verificationBadge.orb_plus')}</title>
          </CheckCircle>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <header
        className="fixed top-0 left-0 right-0 border-b border-white/10 bg-black/60 backdrop-blur-lg shadow-[0_4px_16px_rgba(0,0,0,0.22)]"
        style={{
          zIndex: HEADER_OVERLAY_Z_INDEX,
          height: 'calc(52px + env(safe-area-inset-top))',
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        <div className="h-[52px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
        </div>
      </header>
    );
  }

  return (
    <>
      {isVerifying && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: HEADER_OVERLAY_Z_INDEX }}
        >
          <div className="bg-white rounded-3xl border-4 border-black p-8 shadow-2xl max-w-sm mx-4 transform -translate-y-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-xl font-bold text-gray-700">{t('auth.verifying')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('auth.verifyingDesc')}</p>
              <button
                onClick={() => {
                  setIsVerifying(false);
                  setHasPendingVerification(false);
                  window.location.reload();
                }}
                className="mt-4 text-xs bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-full transition-colors"
              >
                {t('auth.cancelAndRetry')}
              </button>
            </div>
          </div>
        </div>
      )}

      <header
        className="fixed top-0 left-0 right-0 border-b border-white/10 bg-black/60 backdrop-blur-lg shadow-[0_4px_16px_rgba(0,0,0,0.22)]"
        style={{
          zIndex: HEADER_Z_INDEX,
          height: 'calc(52px + env(safe-area-inset-top))',
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        <div className="h-[52px] flex items-center w-full">
          <div className="w-full flex items-center justify-between px-1.5 sm:px-3">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => navigate(-1)}
                className="hidden sm:block p-1.5 hover:bg-white/15 transition-colors text-white rounded-full"
                title="返回"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => navigate('/game')}
                className="hidden sm:block p-1.5 hover:bg-white/15 transition-colors text-white rounded-full"
                title="回到首頁"
              >
                <Home className="w-3.5 h-3.5" />
              </button>

              {registeredUserCount !== null && (
                <div className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 sm:px-2 sm:py-1">
                  <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-300" />
                  <span className="text-[11px] sm:text-xs font-semibold text-white">{registeredUserCount}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0">
              {user ? (
                <>
                  <div
                    className="flex items-center rounded-full"
                    title={
                      user.username
                        ? `${user.username}${user.wallet_address ? ` (${user.wallet_address.slice(0, 6)}...${user.wallet_address.slice(-4)})` : ''}`
                        : t('auth.newUser')
                    }
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/10 rounded-full border border-white/20 flex items-center justify-center overflow-hidden relative">
                      {activeCharacter ? (
                        <CharacterAvatar
                          raceName={activeCharacter.race_name}
                          colorName={activeCharacter.color_name}
                          size={40}
                          showPlanet={false}
                          layerBackgroundUrl={activeCharacter.layer_background_url}
                          layerBodyUrl={activeCharacter.layer_body_url}
                          layerSkinUrl={activeCharacter.layer_skin_url}
                          layerEyesUrl={activeCharacter.layer_eyes_url}
                          layerTattooUrl={activeCharacter.layer_tattoo_url}
                          layerOutfitUrl={activeCharacter.layer_outfit_url}
                          layerFrameUrl={activeCharacter.layer_frame_url}
                          layerArmorId={activeCharacter.layer_armor_id}
                          layerArmorUrl={activeCharacter.layer_armor_url}
                          layerWeaponUrl={activeCharacter.layer_weapon_url}
                          layerShieldUrl={activeCharacter.layer_shield_url}
                          layerAccessoryUrl={activeCharacter.layer_accessory_url}
                          planetImageUrl={activeCharacter.planet_image_url}
                        />
                      ) : (
                        <img
                          src="/image/logo/bbtcoin_newlogo.png"
                          alt="BBT Logo"
                          className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                        />
                      )}

                      {getVerificationBadge() && (
                        <div className="absolute bottom-0 right-0 transform translate-x-0.5 translate-y-0.5">{getVerificationBadge()}</div>
                      )}
                    </div>
                  </div>

                  {bbgBalance !== null && (
                    <button
                      type="button"
                      onClick={() => navigate('/wallet')}
                      className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 sm:px-2 sm:py-1 hover:bg-white/15 transition-colors"
                      title={t('nav.wallet')}
                    >
                      <img
                        src={BBG_TOKEN_ICON}
                        alt="BBG Token"
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 object-contain"
                      />
                      <span className="text-[11px] sm:text-xs font-semibold text-white">{bbgBalance}</span>
                    </button>
                  )}

                  <button
                    onClick={handleLogout}
                    className="hover:bg-white/15 text-white p-1 rounded-full transition-all duration-200 hover:scale-105 flex-shrink-0"
                    title={t('nav.logout')}
                  >
                    <LogOut className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  </button>
                </>
              ) : (
                <>
                  {!isWorldApp && (
                    <button
                      onClick={async () => {
                        await sendLightHapticFeedback();
                        try {
                          await loginWithGoogle();
                        } catch (error) {
                          console.error('Google login error:', error);
                        }
                      }}
                      disabled={isVerifying}
                      className="hover:bg-white/15 text-white px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full transition-all duration-200 hover:scale-105 flex items-center gap-1 sm:gap-1.5 disabled:opacity-50 text-xs sm:text-sm"
                      title={t('auth.loginWithGoogle')}
                    >
                      <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      {t('auth.loginWithGoogle')}
                    </button>
                  )}

                  {isWorldApp && (
                    <button
                      onClick={async () => {
                        await sendLightHapticFeedback();
                        handleMiniKitVerification();
                      }}
                      disabled={isVerifying}
                      className="hover:bg-white/15 text-white px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full transition-all duration-200 hover:scale-105 flex items-center gap-1 sm:gap-1.5 disabled:opacity-50 text-xs sm:text-sm"
                    >
                      <img
                        src="/image/logo/worldcoin.png"
                        alt="Worldcoin"
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 object-contain"
                        style={{ filter: 'invert(1)' }}
                      />
                      {isVerifying ? t('auth.verifying') : t('auth.login')}
                    </button>
                  )}
                </>
              )}

              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className="flex items-center gap-0.5 hover:bg-white/15 rounded-full p-1 transition-colors flex-shrink-0"
                >
                  <Globe className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                  {isLanguageMenuOpen ? (
                    <ChevronUp className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                  ) : (
                    <ChevronDown className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                  )}
                </button>

                {isLanguageMenuOpen && (
                  <div
                    className="absolute top-full right-0 mt-1 bg-black border-2 border-gray-700 rounded-xl shadow-lg p-2 w-[260px] max-w-[calc(100vw-16px)]"
                    style={{ zIndex: HEADER_DROPDOWN_Z_INDEX }}
                  >
                    <div className="grid grid-cols-2 gap-2">
                    {languageOptions.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => changeLanguage(language.code)}
                        className={`w-full h-20 rounded-lg transition-all duration-200 border p-2 flex flex-col items-center justify-center gap-1 hover:scale-105 ${
                          i18n.language === language.code
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-black bg-opacity-50 text-white border-gray-700 hover:bg-opacity-70'
                        }`}
                      >
                        <span className="text-2xl leading-none" aria-hidden="true">
                          {language.flag}
                        </span>
                        <span className="language-label-2line text-xs font-semibold text-center leading-[14px] px-1">
                          {language.label}
                        </span>
                      </button>
                    ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative flex-shrink-0" ref={socialMenuRef}>
                <button
                  onClick={async () => {
                    await sendLightHapticFeedback();
                    setIsSocialMenuOpen(!isSocialMenuOpen);
                  }}
                  className="flex items-center gap-0.5 hover:bg-white/15 rounded-full p-1 transition-colors flex-shrink-0"
                >
                  <Share2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                  {isSocialMenuOpen ? (
                    <ChevronUp className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                  ) : (
                    <ChevronDown className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                  )}
                </button>

                {isSocialMenuOpen && (
                  <div
                    className="absolute top-full right-0 mt-1 bg-black border-2 border-gray-700 rounded-xl shadow-lg p-2 w-64"
                    style={{ zIndex: HEADER_DROPDOWN_Z_INDEX }}
                  >
                    <div className="grid grid-cols-3 gap-2">
                      {gameNavLinks.map((nav, index) => {
                        const IconComponent = nav.icon;
                        return (
                          <Link
                            key={index}
                            to={nav.path}
                            state={nav.state}
                            onClick={async () => {
                              await sendLightHapticFeedback();
                              setIsSocialMenuOpen(false);
                            }}
                            className="flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 w-20 h-20 bg-black bg-opacity-50 text-white hover:bg-opacity-70 hover:scale-105"
                            title={t(`nav.${nav.name}`)}
                          >
                            <IconComponent className="w-6 h-6 mb-1 shrink-0" />
                            <span className="bbg-nav-label-2line text-xs font-semibold text-center leading-[14px] px-1">
                              {t(`nav.${nav.name}`)}
                            </span>
                          </Link>
                        );
                      })}
                    </div>

                    <div className="border-t border-gray-700 my-2" />

                    <div className="grid grid-cols-3 gap-2">
                      {navLinks.map((nav, index) => {
                        const IconComponent = nav.icon;
                        return (
                          <Link
                            key={index}
                            to={nav.path}
                            state={nav.state}
                            onClick={async () => {
                              await sendLightHapticFeedback();
                              setIsSocialMenuOpen(false);
                            }}
                            className="flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 w-20 h-20 bg-black bg-opacity-50 text-white hover:bg-opacity-70 hover:scale-105"
                            title={t(`nav.${nav.name}`)}
                          >
                            <IconComponent className="w-6 h-6 mb-1 shrink-0" />
                            <span className="bbg-nav-label-2line text-xs font-semibold text-center leading-[14px] px-1">
                              {t(`nav.${nav.name}`)}
                            </span>
                          </Link>
                        );
                      })}
                      {socialLinks.map((social, index) => (
                        <a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={async () => {
                            await sendLightHapticFeedback();
                            setIsSocialMenuOpen(false);
                          }}
                          className="flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 w-20 h-20 bg-black bg-opacity-50 text-white hover:bg-opacity-70 hover:scale-105"
                          title={t(`social.${social.name}`)}
                        >
                          <img src={social.icon} alt={t(`social.${social.name}`)} className="w-6 h-6 mb-1" />
                          <span className="bbg-nav-label-2line text-xs font-semibold text-center leading-[14px] px-1">
                            {t(`social.${social.name}`)}
                          </span>
                        </a>
                      ))}
                    </div>

                    <div className="border-t border-gray-700 pt-2 mt-2 text-center">
                      <span className="text-xs text-gray-400 font-semibold">bubble_1.2.0_20260102</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 無 plugin 兩行截斷（只影響 bbg-nav-label-2line） */}
        <style>
          {`
            .bbg-nav-label-2line {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              line-height: 14px;
              max-height: 28px;
              width: 100%;
            }

            .language-label-2line {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              line-height: 14px;
              max-height: 28px;
              width: 100%;
              word-break: break-word;
            }
          `}
        </style>
      </header>
    </>
  );
};

export default Header;
